import { searchClient, ALGOLIA_INDICES, ALGOLIA_FILTERS } from '../../../search/algolia/client';

let cachedTrendingItems = null;
let cachedPopularItems = null;
let prefetchPromise = null;

const PRODUCT_CARD_LIMIT = 4;

function toProductCards(response, indexName, hitsPerPage) {
  return (response.hits || []).slice(0, hitsPerPage).map((hit, indexPosition) => ({
    ...hit,
    __indexName: indexName,
    __queryID: response.queryID || null,
    __position: (Number(response.page) || 0) * hitsPerPage + indexPosition + 1,
  }));
}

export async function fetchProductCards({
  indexName = ALGOLIA_INDICES.products,
  query = '',
  hitsPerPage = PRODUCT_CARD_LIMIT,
  facetFilters = ALGOLIA_FILTERS.hiddenProducts,
} = {}) {
  const response = await searchClient.searchSingleIndex({
    indexName,
    searchParams: {
      query,
      hitsPerPage,
      clickAnalytics: Boolean(query),
      facetFilters,
    },
  });

  return {
    ...response,
    productCards: toProductCards(response, indexName, hitsPerPage),
  };
}

let lastTypingQuery = null;
let typingPromise = null;

/**
 * Batches all typing-state queries (suggestions, collections, articles, products)
 * into a single multi-index search request. Deduplicates by query so all
 * autocomplete sources share the same network call.
 */
export function fetchTypingStateData(query) {
  if (typingPromise && lastTypingQuery === query) return typingPromise;

  lastTypingQuery = query;
  typingPromise = searchClient
    .search({
      requests: [
        { indexName: ALGOLIA_INDICES.querySuggestions, query, hitsPerPage: 6 },
        { indexName: ALGOLIA_INDICES.collections, query, hitsPerPage: 5 },
        { indexName: ALGOLIA_INDICES.articles, query, hitsPerPage: 5 },
        {
          indexName: ALGOLIA_INDICES.products,
          query,
          hitsPerPage: PRODUCT_CARD_LIMIT,
          clickAnalytics: true,
          facetFilters: ALGOLIA_FILTERS.hiddenProducts,
        },
      ],
    })
    .then(({ results }) => {
      const [suggestionsResult, collectionsResult, articlesResult, productsResult] = results;
      return {
        suggestions: suggestionsResult.hits,
        collections: collectionsResult.hits.map((hit) => ({ ...hit, _itemType: 'collection' })),
        articles: articlesResult.hits.map((hit) => ({ ...hit, _itemType: 'blog' })),
        products: {
          ...productsResult,
          productCards: toProductCards(productsResult, ALGOLIA_INDICES.products, PRODUCT_CARD_LIMIT),
        },
      };
    });

  return typingPromise;
}

export function useSearchData() {
  const prefetchEmptyStateData = () => {
    if (prefetchPromise) return prefetchPromise;

    prefetchPromise = searchClient
      .search({
        requests: [
          { indexName: ALGOLIA_INDICES.querySuggestions, query: '', hitsPerPage: 5 },
          { indexName: ALGOLIA_INDICES.popularProducts, query: '', hitsPerPage: PRODUCT_CARD_LIMIT, facetFilters: ALGOLIA_FILTERS.hiddenProducts },
        ],
      })
      .then(({ results }) => {
        const [suggestionsResult, popularResult] = results;
        cachedTrendingItems = suggestionsResult.hits;
        cachedPopularItems = toProductCards(popularResult, ALGOLIA_INDICES.popularProducts, PRODUCT_CARD_LIMIT);
        return [cachedTrendingItems, cachedPopularItems];
      });

    return prefetchPromise;
  };

  const getTrendingItems = async () => {
    if (cachedTrendingItems) return cachedTrendingItems;
    await prefetchEmptyStateData();
    return cachedTrendingItems || [];
  };

  const getPopularItems = async () => {
    if (cachedPopularItems) return cachedPopularItems;
    await prefetchEmptyStateData();
    return cachedPopularItems || [];
  };

  return { prefetchEmptyStateData, getTrendingItems, getPopularItems };
}
